import { useEffect, useState, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import {
  ArrowUpRight, Menu, X, MapPin, MessageCircle, Mountain, ShieldCheck, Wrench,
  Sparkles, CalendarDays, LayoutDashboard, LogOut, Compass, Package, Image as ImageIcon,
  Star, Settings as SettingsIcon, Mail, Instagram, Trash2, Plus, Save, Eye,
  Palette, Upload, Globe, RotateCcw,
} from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK = {
  hero: "https://images.unsplash.com/photo-1602388811169-85acb00331ea?auto=format&fit=crop&w=1800&q=85",
  shelter: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?auto=format&fit=crop&w=1600&q=85",
  adventure: "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1600&q=85",
  wash: "https://images.unsplash.com/photo-1511765224389-37f0e77cf0eb?auto=format&fit=crop&w=1600&q=85",
  repair: "https://images.unsplash.com/photo-1570752321219-41822a21a761?auto=format&fit=crop&w=1600&q=85",
  event: "https://images.unsplash.com/photo-1526491109672-74740652b963?auto=format&fit=crop&w=1600&q=85",
  about: "https://images.unsplash.com/photo-1517940310602-26535839fe84?auto=format&fit=crop&w=1600&q=85",
  gallery: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1600&q=85",
};

const UNITS = [
  { slug: "shelter", title: "Gembeling Shelter", label: "Camping & Hiking Equipment Rental",
    text: "Perlengkapan yang siap menemani kebutuhan personal, komunitas, sekolah, dan event.", icon: Mountain,
    seo: "Gembeling Shelter | Sewa Alat Camping & Hiking" },
  { slug: "adventure", title: "Gembeling Adventure", label: "Open Trip & Trip Organizer",
    text: "Perjalanan yang lebih terencana, nyaman, dan berkesan bersama tim berpengalaman.", icon: Sparkles,
    seo: "Gembeling Adventure | Open Trip & Trip Organizer" },
  { slug: "wash", title: "Gembeling Wash", label: "Outdoor Gear Cleaning & Care",
    text: "Menjaga perlengkapan tetap bersih dan siap untuk perjalanan berikutnya.", icon: ShieldCheck,
    seo: "Gembeling Wash | Outdoor Gear Cleaning" },
  { slug: "repair", title: "Gembeling Repair", label: "Outdoor Gear Repair & Maintenance",
    text: "Repair, maintenance, dan pemeriksaan equipment untuk memperpanjang masa pakai.", icon: Wrench,
    seo: "Gembeling Repair | Outdoor Gear Repair" },
  { slug: "event", title: "Gembeling Vendor Camping", label: "Outdoor Event Support & Camping Vendor",
    text: "Dukungan perlengkapan outdoor untuk EO, perusahaan, sekolah, komunitas, dan gathering.", icon: CalendarDays,
    seo: "Gembeling Vendor Camping | Outdoor Event Support" },
];

const wa = (num, text = "Halo Gembeling Group, saya ingin konsultasi.") =>
  `https://wa.me/62${(num || "").replace(/\D/g, "").replace(/^62/, "").replace(/^0/, "")}?text=${encodeURIComponent(text)}`;

function useSEO(title, description) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    const prevDesc = meta.content;
    meta.content = description;
    return () => { document.title = prev; meta.content = prevDesc; };
  }, [title, description]);
}

function useSite() {
  const [site, setSite] = useState({ branches: [], settings: {}, branding: {}, products: [], trips: [], gallery: [], testimonials: [], services: [], loaded: false });
  useEffect(() => {
    fetch(`${API}/public/site`)
      .then(r => r.json())
      .then(d => setSite({ ...d, loaded: true }))
      .catch(() => setSite(s => ({ ...s, loaded: true })));
  }, []);
  return site;
}

function resolveImage(url, fallback) {
  if (!url) return fallback;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

function useFontPreset(preset) {
  useEffect(() => {
    document.documentElement.setAttribute("data-font", preset || "classic");
  }, [preset]);
}

function Logo({ branding }) {
  const img = resolveImage(branding?.logo_image_url, null);
  if (img) return <img src={img} alt="Gembeling Group" className="brand-logo-img" data-testid="brand-logo-image" />;
  return (
    <>
      <span className="brand-mark">G</span>
      <span>{branding?.logo_text_primary || "GEMBELING"} <b>{branding?.logo_text_secondary || "GROUP"}</b></span>
    </>
  );
}

// Render headline with support for *italic* word wrapped in asterisks and \n line breaks
function renderHeadline(text) {
  if (!text) return null;
  const lines = text.split(/\n|(?<=\.)(?=\s[A-Z])/).map(l => l.trim()).filter(Boolean);
  return lines.map((line, i) => (
    <span key={i} style={{ display: "block" }}>
      {line.split(/(\*[^*]+\*)/).map((part, j) => {
        if (part.startsWith("*") && part.endsWith("*")) return <em key={j}>{part.slice(1, -1)}</em>;
        return <span key={j}>{part}</span>;
      })}
    </span>
  ));
}

function Header({ branding }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = [["/", "HOME"], ["/about", "ABOUT"], ["/#business", "BUSINESS"], ["/shelter", "SHELTER"],
               ["/adventure", "ADVENTURE"], ["/gallery", "GALLERY"], ["/event", "VENDOR"], ["/contact", "CONTACT"]];
  return (
    <header className="header" data-testid="site-header">
      <Link to="/" className="brand" data-testid="brand-link"><Logo branding={branding} /></Link>
      <button className="menu-button" onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Menu">
        {open ? <X /> : <Menu />}
      </button>
      <nav className={open ? "nav open" : "nav"}>
        {nav.map(([to, label]) => (
          <Link key={label} onClick={() => setOpen(false)} className={loc.pathname === to ? "active" : ""}
                to={to} data-testid={`nav-${label.toLowerCase()}`}>{label}</Link>
        ))}
        <Link className="nav-cta" to="/inquiry" onClick={() => setOpen(false)} data-testid="nav-get-started">
          GET STARTED <ArrowUpRight size={15} />
        </Link>
      </nav>
    </header>
  );
}

function WhatsAppFloat({ number }) {
  return (
    <a className="wa-float" href={wa(number || "087852477016")} target="_blank" rel="noreferrer" data-testid="floating-whatsapp">
      <MessageCircle size={21} /><span>WhatsApp</span>
    </a>
  );
}

function Footer({ settings, branding }) {
  return (
    <footer className="footer">
      <div>
        <div className="brand footer-brand"><Logo branding={branding} /></div>
        <p>Outdoor • Adventure • Services</p>
        {settings.email && <p className="footer-contact"><Mail size={13} /> {settings.email}</p>}
      </div>
      <div className="footer-links">
        <Link to="/about" data-testid="footer-about">About</Link>
        <Link to="/gallery" data-testid="footer-gallery">Gallery</Link>
        <Link to="/inquiry" data-testid="footer-inquiry">Inquiry</Link>
        <Link to="/contact" data-testid="footer-contact">Contact</Link>
      </div>
      <small>© 2026 Gembeling Group. Built for the journey.</small>
    </footer>
  );
}

function Shell({ children, settings = {}, branding = {} }) {
  useFontPreset(branding.font_preset);
  return (
    <>
      <Header branding={branding} />
      {children}
      <WhatsAppFloat number={settings.whatsapp} />
      <Footer settings={settings} branding={branding} />
    </>
  );
}

function PageHero({ eyebrow, title, text, image, loaded }) {
  return (
    <section className="page-hero">
      {loaded && image ? <img src={image} alt="Outdoor adventure" /> : <div className="page-hero-placeholder" />}
      <div className="page-hero-copy">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {text && <p className="hero-text">{text}</p>}
      </div>
    </section>
  );
}

function Home() {
  const site = useSite();
  const b = site.branding || {};
  useSEO("Gembeling Group | Outdoor, Adventure & Services",
    "Gembeling Group menghadirkan outdoor equipment rental, open trip, trip organizer, event support, wash dan repair untuk berbagai kebutuhan adventure.");
  const stats = [
    { v: site.branches.length || 2, l: "Rental Locations" },
    { v: "05", l: "Business Services" },
    site.products.length > 0 && { v: `${site.products.length}+`, l: "Equipment Items" },
    site.trips.length > 0 && { v: `${site.trips.length}+`, l: "Curated Trips" },
  ].filter(Boolean);
  const headline = b.hero_headline || "Build for adventure. Ready for the journey.";
  const subheadline = b.hero_subheadline ||
    "Gembeling Group menghadirkan layanan outdoor, adventure, equipment rental, trip organizer, serta berbagai layanan pendukung untuk membantu setiap perjalanan menjadi lebih mudah dan siap.";
  const eyebrowRaw = b.hero_eyebrow || "Rent Equipment • Trip Organizer • Wash • Repair • Vendor Camping";
  const eyebrow = eyebrowRaw.replace(/\s*,\s*/g, " • ").replace(/\s*·\s*/g, " • ");
  // Only show image once branding loaded, to avoid stock fallback flash
  const heroImg = site.loaded ? (b.hero_image_url ? resolveImage(b.hero_image_url, FALLBACK.hero) : FALLBACK.hero) : null;
  return (
    <Shell settings={site.settings} branding={site.branding}>
      <main>
        <section className="hero">
          <div className="hero-image" style={heroImg ? { backgroundImage: `linear-gradient(90deg,rgba(8,29,22,.92) 0%,rgba(8,29,22,.61) 45%,rgba(8,29,22,.08) 100%),url(${heroImg})` } : { background: "linear-gradient(90deg,#0c211a 0%,#122c23 55%,#1a3a2e 100%)" }} />
          <div className="hero-copy">
            <p className="eyebrow" data-testid="hero-eyebrow">{eyebrow.toUpperCase()}</p>
            <h1 data-testid="hero-headline">{renderHeadline(headline)}</h1>
            <p className="hero-text" data-testid="hero-subheadline">{subheadline}</p>
            <div className="actions">
              <a className="button primary" href="#business" data-testid="hero-explore-button">EXPLORE OUR BUSINESS <ArrowUpRight size={17} /></a>
              <Link className="button ghost" to="/contact" data-testid="hero-contact-button">CONTACT US</Link>
            </div>
          </div>
          <div className="hero-caption">GEMBELING GROUP / 2026</div>
        </section>

        <section className="intro band">
          <div className="section-kicker">01 / THE GROUP</div>
          <div>
            <h2>One group.<br /><span>Multiple outdoor solutions.</span></h2>
            <p className="lead">Gembeling Group mengembangkan berbagai layanan yang saling terhubung dalam satu ekosistem outdoor dan adventure — mulai dari perlengkapan, perjalanan, event, hingga perawatan equipment.</p>
            <div className="stat-row">
              {stats.map((s, i) => (
                <div key={i} data-testid={`stat-${i}`}><strong>{s.v}</strong><span>{s.l}</span></div>
              ))}
            </div>
          </div>
        </section>

        <section id="business" className="business band">
          <div className="section-kicker">02 / OUR BUSINESS</div>
          <div className="section-heading">
            <h2>Designed around<br /><span>your journey.</span></h2>
            <p>Berbagai layanan untuk kebutuhan outdoor dan adventure dalam satu ekosistem.</p>
          </div>
          <div className="unit-grid">
            {UNITS.map((u, i) => (
              <Link to={`/${u.slug}`} className={`unit-card unit-${i}`} key={u.slug} data-testid={`business-card-${u.slug}`}>
                <div className="unit-top"><u.icon size={25} /><span>0{i + 1}</span></div>
                <h3>{u.title}</h3>
                <p>{u.label}</p>
                <div className="unit-bottom"><span>{u.text}</span><ArrowUpRight size={18} /></div>
              </Link>
            ))}
          </div>
        </section>

        <section className="image-band">
          {site.loaded ? <img src={resolveImage(b.about_image_url, FALLBACK.about)} alt="Hiker preparing for an outdoor journey" /> : <div className="image-band-placeholder" />}
          <div>
            <p className="eyebrow">THE ECOSYSTEM</p>
            <h2>More than equipment.<br /><em>We build experiences.</em></h2>
            <p className="hero-text">Dari persiapan hingga pulang kembali ke rumah — Gembeling hadir di setiap langkah perjalananmu.</p>
            <Link to="/about" className="text-link" data-testid="home-about-link">Discover our story <ArrowUpRight size={16} /></Link>
          </div>
        </section>

        {site.testimonials.length > 0 && (
          <section className="band testimonial-section">
            <div className="section-kicker">TRUSTED BY ADVENTURERS</div>
            <div>
              <h2>Trusted by<br /><span>fellow explorers.</span></h2>
              <div className="testimonial-grid">
                {site.testimonials.map(t => (
                  <div key={t.id} className="testimonial-card" data-testid={`testimonial-${t.id}`}>
                    <div className="stars">{"★".repeat(Math.max(1, Math.min(5, t.rating || 5)))}</div>
                    <p>{t.description}</p>
                    <div className="t-meta"><b>{t.name}</b><span>{t.service || t.role}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section id="services" className="cta-band">
          <p className="eyebrow">START YOUR NEXT CHAPTER</p>
          <h2>Let's plan your<br /><em>next adventure.</em></h2>
          <Link to="/inquiry" className="button light" data-testid="home-inquiry-button">TALK TO GEMBELING <ArrowUpRight size={17} /></Link>
        </section>
      </main>
    </Shell>
  );
}

function About() {
  const site = useSite();
  useSEO("About Gembeling Group | Outdoor Ecosystem",
    "Mengenal Gembeling Group — ekosistem outdoor yang menghubungkan rental, adventure, event, wash, dan repair dalam satu layanan terpadu.");
  const vision = site.settings.vision || "";
  const mission = site.settings.mission || "";
  return (
    <Shell settings={site.settings} branding={site.branding}>
      <PageHero eyebrow="ABOUT GEMBELING GROUP"
        title={<>More than equipment.<br /><em>We build experiences.</em></>}
        text="Gembeling Group berkembang dari kebutuhan sederhana di dunia outdoor dan adventure menjadi sebuah ekosistem layanan yang saling terhubung."
        image={resolveImage(site.branding?.about_image_url, FALLBACK.about)} loaded={site.loaded} />
      <section className="content band about-copy">
        <div className="section-kicker">OUR STORY</div>
        <div>
          <p className="lead">Melalui Gembeling Shelter, Gembeling Adventure, Gembeling Wash, Gembeling Repair, dan Gembeling Vendor Camping, kami berupaya memberikan solusi yang lebih lengkap bagi pelanggan yang ingin melakukan perjalanan, camping, hiking, maupun kegiatan outdoor lainnya.</p>
          <p className="lead">Kami percaya bahwa perjalanan yang baik dimulai dari persiapan yang baik. Gembeling Group hadir untuk membantu mempersiapkannya.</p>
        </div>
      </section>

      {(vision || mission) && (
        <section className="band vm-section">
          <div className="section-kicker">VISION & MISSION</div>
          <div className="vm-grid">
            {vision && (
              <div className="vm-card" data-testid="vision-card">
                <div className="vm-tag">01 · VISI</div>
                <h3>Our Vision</h3>
                <p>{vision}</p>
              </div>
            )}
            {mission && (
              <div className="vm-card" data-testid="mission-card">
                <div className="vm-tag">02 · MISI</div>
                <h3>Our Mission</h3>
                <p>{mission}</p>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="band ecosystem-section">
        <div className="section-kicker">OUR ECOSYSTEM</div>
        <div>
          <h2>One connected<br /><span>outdoor journey.</span></h2>
          <div className="eco-flow">
            {["CUSTOMER", "ADVENTURE · TRIP", "SHELTER · EQUIPMENT", "VENDOR CAMPING SUPPORT", "WASH · CLEANING", "REPAIR · MAINTENANCE"].map((x, i) => (
              <div key={x}><span>0{i + 1}</span><b>{x}</b></div>
            ))}
            <div className="eco-end"><ShieldCheck size={18} /><b>READY FOR THE NEXT ADVENTURE</b></div>
          </div>
        </div>
      </section>
    </Shell>
  );
}

function ShelterPage() {
  const site = useSite();
  const u = UNITS[0];
  useSEO(u.seo, "Sewa alat camping, sewa tenda, dan rental perlengkapan hiking untuk kebutuhan personal, komunitas, sekolah, dan event. 2 cabang di Depok & Cileungsi.");
  const [branchFilter, setBranchFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const branches = site.branches;
  const categories = useMemo(() => ["all", ...new Set(site.products.map(p => p.category).filter(Boolean))], [site.products]);
  const products = site.products.filter(p =>
    (branchFilter === "all" || !p.branch_id || p.branch_id === branchFilter) &&
    (category === "all" || p.category === category));
  return (
    <Shell settings={site.settings} branding={site.branding}>
      <PageHero eyebrow="GEMBELING SHELTER"
        title={<>Camping & hiking<br /><em>equipment rental.</em></>}
        text="Perlengkapan outdoor yang siap menemani perjalananmu."
        image={resolveImage(site.branding?.shelter_image_url, FALLBACK.shelter)} loaded={site.loaded} />

      <section className="band locations">
        <div className="section-kicker">OUR LOCATIONS</div>
        <div>
          <h2>Choose your<br /><span>starting point.</span></h2>
          <div className="location-grid">
            {branches.map(b => (
              <div className="location" key={b.id} data-testid={`branch-${b.id}`}>
                <div className="status">● {b.status}</div>
                <h3>{b.name.replace("Gembeling Shelter — ", "")}</h3>
                <p>{b.description}</p>
                <p className="muted"><MapPin size={15} /> {b.address}</p>
                <p className="muted">{b.hours}</p>
                <div className="location-actions">
                  {b.maps_url && <a href={b.maps_url} target="_blank" rel="noreferrer" className="text-link" data-testid={`branch-map-${b.id}`}>Open Maps <ArrowUpRight size={15} /></a>}
                  <a href={wa(b.whatsapp, `Halo ${b.name}, saya ingin booking rental.`)} target="_blank" rel="noreferrer" className="button small" data-testid={`branch-book-${b.id}`}>BOOKING <MessageCircle size={14} /></a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band catalog-section">
        <div className="section-kicker">SHELTER CATALOG</div>
        <div>
          <div className="section-heading">
            <h2>Equipment<br /><span>ready to rent.</span></h2>
            <p>Produk dengan status MAINTENANCE tidak dapat dibooking.</p>
          </div>
          <div className="filters">
            <div className="filter-group" data-testid="branch-filter">
              <span>LOCATION</span>
              <button className={branchFilter === "all" ? "active" : ""} onClick={() => setBranchFilter("all")} data-testid="filter-branch-all">All</button>
              {branches.map(b => (
                <button key={b.id} className={branchFilter === b.id ? "active" : ""} onClick={() => setBranchFilter(b.id)} data-testid={`filter-branch-${b.id}`}>
                  {b.name.replace("Gembeling Shelter — ", "")}
                </button>
              ))}
            </div>
            <div className="filter-group" data-testid="category-filter">
              <span>CATEGORY</span>
              {categories.map(c => (
                <button key={c} className={category === c ? "active" : ""} onClick={() => setCategory(c)} data-testid={`filter-cat-${c}`}>
                  {c === "all" ? "All" : c}
                </button>
              ))}
            </div>
          </div>

          {products.length === 0 ? (
            <div className="empty-admin" data-testid="shelter-empty">Katalog akan ditampilkan setelah admin menambahkan equipment melalui panel admin.</div>
          ) : (
            <div className="catalog-grid">
              {products.map(p => (
                <div key={p.id} className={`catalog-card ${p.status === "MAINTENANCE" ? "disabled" : ""}`} data-testid={`product-${p.id}`}>
                  <div className="catalog-img" style={{ backgroundImage: `url(${resolveImage(p.image_url, FALLBACK.shelter)})` }}>
                    <span className={`status-tag ${(p.status || "AVAILABLE").toLowerCase()}`}>{p.status || "AVAILABLE"}</span>
                  </div>
                  <div className="catalog-meta">
                    <small>{p.category}{p.capacity ? ` · ${p.capacity}` : ""}</small>
                    <h4>{p.name}</h4>
                    <p>{p.description}</p>
                    <div className="catalog-bottom">
                      <b>{p.price || "Hubungi"}</b>
                      {p.status !== "MAINTENANCE" ? (
                        <a href={wa(branches.find(b => b.id === p.branch_id)?.whatsapp || site.settings.whatsapp,
                          `Halo Gembeling Shelter, saya ingin booking ${p.name}.`)}
                          target="_blank" rel="noreferrer" className="button small" data-testid={`product-book-${p.id}`}>
                          BOOK <ArrowUpRight size={13} />
                        </a>
                      ) : <span className="muted-tag">Maintenance</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <InquiryBanner />
    </Shell>
  );
}

function AdventurePage() {
  const site = useSite();
  const u = UNITS[1];
  useSEO(u.seo, "Dokumentasi trip dan perjalanan yang pernah diorganisir oleh Gembeling Adventure.");
  const [cat, setCat] = useState("ALL");
  const trips = cat === "ALL" ? site.trips : site.trips.filter(t => (t.category || "").toUpperCase() === cat);
  const categories = ["OPEN TRIP", "PRIVATE TRIP", "CORPORATE TRIP", "COMMUNITY TRIP", "SCHOOL TRIP"];
  const scrollToDocs = (c) => {
    setCat(c);
    setTimeout(() => {
      document.getElementById("trip-docs")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };
  return (
    <Shell settings={site.settings} branding={site.branding}>
      <PageHero eyebrow="GEMBELING ADVENTURE"
        title={<>Your next adventure<br /><em>starts here.</em></>}
        text="Open Trip & Trip Organizer untuk perjalanan yang lebih terencana, nyaman, dan berkesan."
        image={resolveImage(site.branding?.adventure_image_url, FALLBACK.adventure)} loaded={site.loaded} />

      <section className="band service-detail">
        <div className="section-kicker">TRIP CATEGORIES</div>
        <div>
          <h2>Perjalanan<br /><span>untuk setiap kebutuhan.</span></h2>
          <div className="service-list">
            {categories.map((x, i) => (
              <button key={x} className="service-list-row" onClick={() => scrollToDocs(x)}
                data-testid={`cat-link-${x.replace(/\s+/g, "-").toLowerCase()}`}>
                <span>0{i + 1}</span><b>{x}</b><ArrowUpRight size={17} />
              </button>
            ))}
          </div>
        </div>
      </section>

      <section id="trip-docs" className="band catalog-section">
        <div className="section-kicker">TRIP DOCUMENTATION</div>
        <div>
          <div className="section-heading">
            <h2>Moments<br /><span>from past journeys.</span></h2>
            <p>Dokumentasi perjalanan yang pernah kami organisir — ingin trip serupa? Konsultasikan ke tim kami.</p>
          </div>
          <div className="filters">
            <div className="filter-group" data-testid="trip-cat-filter">
              <span>CATEGORY</span>
              <button className={cat === "ALL" ? "active" : ""} onClick={() => setCat("ALL")} data-testid="trip-filter-ALL">All</button>
              {categories.map(c => (
                <button key={c} className={cat === c ? "active" : ""} onClick={() => setCat(c)} data-testid={`trip-filter-${c.replace(/\s+/g, "-")}`}>{c}</button>
              ))}
            </div>
          </div>
          {trips.length === 0 ? (
            <div className="empty-admin" data-testid="trip-empty">Dokumentasi trip untuk kategori ini akan tampil setelah admin mengunggahnya.</div>
          ) : (
            <div className="gallery-grid">
              {trips.map(t => (
                <a key={t.id} href={resolveImage(t.image_url, FALLBACK.adventure)} target="_blank" rel="noreferrer" className="gallery-item" data-testid={`trip-${t.id}`}>
                  <img src={resolveImage(t.image_url, FALLBACK.adventure)} alt={t.name} loading="lazy" />
                  <div className="gallery-cap">
                    <small>{t.destination || t.category}{t.date ? ` · ${t.date}` : ""}</small>
                    <b>{t.name}</b>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <InquiryBanner cta="PLAN YOUR TRIP WITH US" />
    </Shell>
  );
}

function ServicePriceList({ type, services, settings }) {
  const items = services.filter(s => s.category === type);
  const labels = {
    wash: ["WASH PRICE LIST", "Harga standar perawatan equipment"],
    repair: ["REPAIR PRICE LIST", "Estimasi biaya perbaikan — final price disesuaikan setelah inspeksi."],
    event: ["EVENT CAMPING PACKAGES", "Paket dan harga vendor camping — disesuaikan dengan kebutuhan event."],
  }[type] || ["PRICE LIST", ""];
  return (
    <section className="band price-list-section" id="service-price-list">
      <div className="section-kicker">{labels[0]}</div>
      <div>
        <div className="section-heading">
          <h2>Harga<br /><span>yang transparan.</span></h2>
          <p>{labels[1]}</p>
        </div>
        {items.length === 0 ? (
          <div className="empty-admin" data-testid={`service-${type}-empty`}>Daftar harga akan tampil setelah admin mengisi lewat panel admin.</div>
        ) : (
          <div className="price-list">
            {items.map(s => (
              <div key={s.id} className="price-row" data-testid={`service-${s.id}`}>
                <div className="price-info">
                  <b>{s.name}</b>
                  {s.description && <p>{s.description}</p>}
                </div>
                <div className="price-amount">
                  <strong>{s.price || "Hubungi"}</strong>
                  <a href={wa(settings.whatsapp, `Halo Gembeling, saya tertarik dengan layanan ${s.name}.`)}
                     target="_blank" rel="noreferrer" className="button small" data-testid={`service-book-${s.id}`}>
                    CONSULT <ArrowUpRight size={12} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ServiceGallery({ category, items }) {
  const photos = items.filter(g => (g.category || "").toLowerCase() === category.toLowerCase());
  if (photos.length === 0) return null;
  return (
    <section className="band gallery-band">
      <div className="section-kicker">PHOTO DOCUMENTATION</div>
      <div>
        <div className="section-heading">
          <h2>Our camps<br /><span>in action.</span></h2>
          <p>Dokumentasi event dan vendor camping Gembeling.</p>
        </div>
        <div className="gallery-grid">
          {photos.map(g => (
            <a key={g.id} href={resolveImage(g.image_url, "")} target="_blank" rel="noreferrer" className="gallery-item" data-testid={`event-photo-${g.id}`}>
              <img src={resolveImage(g.image_url, "")} alt={g.name} loading="lazy" />
              <div className="gallery-cap">
                <small>{g.category}</small>
                <b>{g.name}</b>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicePage({ type }) {
  const site = useSite();
  const u = UNITS.find(x => x.slug === type);
  useSEO(u.seo, u.text);
  const details = {
    wash: ["Tent cleaning", "Sleeping bag cleaning", "Outdoor equipment cleaning", "Gear care", "Post-trip cleaning"],
    repair: ["Tent repair", "Equipment repair", "Maintenance", "Inspection", "Outdoor gear restoration"],
    event: ["Camping equipment", "Tent equipment", "Outdoor equipment", "Event equipment", "Bulk rental"],
  }[type];
  const headlines = {
    wash: <>Keep your gear<br /><em>ready for the next adventure.</em></>,
    repair: <>Repair. Restore.<br /><em>Adventure again.</em></>,
    event: <>We help you<br /><em>build the camp.</em></>,
  }[type];
  const packages = type === "event"
    ? ["50 Participants", "100 Participants", "200 Participants", "200+ Participants"] : null;
  return (
    <Shell settings={site.settings} branding={site.branding}>
      <PageHero eyebrow={type === "event" ? "GEMBELING VENDOR CAMPING" : `GEMBELING ${type.toUpperCase()}`} title={headlines} text={u.text} image={resolveImage(site.branding?.[`${type}_image_url`], FALLBACK[type])} loaded={site.loaded} />
      <section className="band service-detail">
        <div className="section-kicker">WHAT WE DO</div>
        <div>
          <h2>{u.title}<br /><span>for every kind of journey.</span></h2>
          <div className="service-list">
            {details.map((x, i) => (
              <button key={x} className="service-list-row" onClick={() => document.getElementById("service-price-list")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                data-testid={`service-item-${i}`}>
                <span>0{i + 1}</span><b>{x}</b><ArrowUpRight size={17} />
              </button>
            ))}
          </div>
        </div>
      </section>
      {packages && (
        <section className="band packages-section">
          <div className="section-kicker">INQUIRY SIZE</div>
          <div>
            <h2>Tell us the<br /><span>scale of your event.</span></h2>
            <div className="package-grid">
              {packages.map(p => (
                <Link to="/inquiry" key={p} className="package-card" data-testid={`pkg-${p}`}>
                  <b>{p}</b><ArrowUpRight size={17} />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <ServicePriceList type={type} services={site.services} settings={site.settings} />
      {type === "event" && <ServiceGallery category="Event" items={site.gallery} />}
      <InquiryBanner cta={type === "event" ? "TALK TO OUR VENDOR TEAM" : `CONSULT ${type.toUpperCase()} SERVICE`} />
    </Shell>
  );
}

function GalleryPage() {
  const site = useSite();
  useSEO("Gallery | Gembeling Group", "Dokumentasi kegiatan outdoor dan adventure Gembeling Group — trip, event, camping, hiking, wash, dan repair.");
  const [filter, setFilter] = useState("ALL");
  const categories = useMemo(() => ["ALL", ...new Set(site.gallery.map(g => (g.category || "").toUpperCase()).filter(Boolean))], [site.gallery]);
  const items = filter === "ALL" ? site.gallery : site.gallery.filter(g => (g.category || "").toUpperCase() === filter);
  return (
    <Shell settings={site.settings} branding={site.branding}>
      <PageHero eyebrow="GEMBELING IN ACTION"
        title={<>Moments from the<br /><em>outdoor.</em></>}
        text="Dokumentasi perjalanan, trip, dan kegiatan outdoor Gembeling Group."
        image={resolveImage(site.branding?.gallery_image_url, FALLBACK.gallery)} loaded={site.loaded} />
      <section className="band gallery-band">
        <div className="section-kicker">GALLERY</div>
        <div>
          <div className="filters">
            <div className="filter-group">
              <span>FILTER</span>
              {categories.map(c => (
                <button key={c} className={filter === c ? "active" : ""} onClick={() => setFilter(c)} data-testid={`gallery-filter-${c}`}>{c}</button>
              ))}
            </div>
          </div>
          {items.length === 0 ? (
            <div className="empty-admin" data-testid="gallery-empty">Belum ada foto. Admin dapat menambahkan dokumentasi melalui panel admin.</div>
          ) : (
            <div className="gallery-grid">
              {items.map(g => (
                <a key={g.id} href={resolveImage(g.image_url, "")} target="_blank" rel="noreferrer" className="gallery-item" data-testid={`gallery-${g.id}`}>
                  <img src={resolveImage(g.image_url, "")} alt={g.name} loading="lazy" />
                  <div className="gallery-cap">
                    <small>{g.category}</small>
                    <b>{g.name}</b>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    </Shell>
  );
}

function Contact() {
  const site = useSite();
  const s = site.settings;
  useSEO("Contact | Gembeling Group", "Hubungi Gembeling Group untuk kebutuhan rental, trip, event, wash, dan repair outdoor.");
  return (
    <Shell settings={s}>
      <PageHero eyebrow="CONTACT"
        title={<>Let's plan your<br /><em>next adventure.</em></>}
        text="Hubungi tim Gembeling untuk rental, trip, event, wash, atau repair."
        image={resolveImage(site.branding?.about_image_url, FALLBACK.about)} loaded={site.loaded} />
      <section className="band contact-grid">
        <div className="section-kicker">GET IN TOUCH</div>
        <div>
          <h2>We're ready<br /><span>when you are.</span></h2>
          <div className="contact-list">
            {s.whatsapp && (
              <a href={wa(s.whatsapp)} target="_blank" rel="noreferrer" data-testid="contact-whatsapp">
                <MessageCircle /> WhatsApp <b>{s.whatsapp}</b>
              </a>
            )}
            {s.email && <a href={`mailto:${s.email}`} data-testid="contact-email"><Mail /> Email <b>{s.email}</b></a>}
            {s.instagram && (
              <a href={`https://instagram.com/${s.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" data-testid="contact-instagram">
                <Instagram /> Instagram <b>{s.instagram}</b>
              </a>
            )}
            {s.maps_url && (
              <a href={s.maps_url} target="_blank" rel="noreferrer" data-testid="contact-maps">
                <MapPin /> {s.address || "Lokasi"} <b>Open Google Maps</b>
              </a>
            )}
          </div>
          <Link to="/inquiry" className="button primary" data-testid="contact-inquiry-button">CONTACT GEMBELING <ArrowUpRight size={17} /></Link>
        </div>
      </section>

      <section className="band locations">
        <div className="section-kicker">OUR LOCATIONS</div>
        <div>
          <h2>Visit a<br /><span>Gembeling Shelter.</span></h2>
          <div className="location-grid">
            {site.branches.map(b => (
              <div className="location" key={b.id} data-testid={`contact-branch-${b.id}`}>
                <div className="status">● {b.status}</div>
                <h3>{b.name.replace("Gembeling Shelter — ", "")}</h3>
                <p className="muted"><MapPin size={15} /> {b.address}</p>
                <p className="muted">{b.hours}</p>
                <div className="location-actions">
                  {b.maps_url && <a href={b.maps_url} target="_blank" rel="noreferrer" className="text-link">Open Maps <ArrowUpRight size={15} /></a>}
                  <a href={wa(b.whatsapp)} target="_blank" rel="noreferrer" className="button small">WHATSAPP <MessageCircle size={14} /></a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Shell>
  );
}

function InquiryBanner({ cta = "START AN INQUIRY" }) {
  return (
    <section className="cta-band compact">
      <p className="eyebrow">HAVE A PLAN?</p>
      <h2>Tell us what<br /><em>you're building.</em></h2>
      <Link to="/inquiry" className="button light" data-testid="service-inquiry-button">{cta} <ArrowUpRight size={17} /></Link>
    </section>
  );
}

function Inquiry() {
  const site = useSite();
  const [form, setForm] = useState({ service: "Rental" });
  const [done, setDone] = useState(false);
  const update = e => setForm({ ...form, [e.target.name]: e.target.value });
  useSEO("Booking / Inquiry | Gembeling Group", "Kirim inquiry untuk rental, trip, event, wash, atau repair Gembeling Group.");
  const submit = async e => {
    e.preventDefault();
    await fetch(`${API}/inquiries`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setDone(true);
    const target = form.branch && site.branches.find(b => b.id === form.branch)?.whatsapp;
    const num = target || site.settings.whatsapp || "087852477016";
    window.location.href = wa(num,
      `Halo Gembeling Group, saya ${form.name}. Inquiry layanan ${form.service}${form.branch ? ` di cabang ${form.branch}` : ""}${form.date ? ` tanggal ${form.date}` : ""}${form.people ? `, ${form.people} orang` : ""}. ${form.notes || ""}`);
  };
  return (
    <Shell settings={site.settings} branding={site.branding}>
      <section className="form-page">
        <div className="form-intro">
          <p className="eyebrow">BOOKING / INQUIRY</p>
          <h1>Make the first<br /><em>move.</em></h1>
          <p>Isi kebutuhanmu. Setelah submit, kamu akan diarahkan ke WhatsApp tim Gembeling untuk melanjutkan percakapan.</p>
        </div>
        {done ? (
          <div className="success" data-testid="inquiry-success">
            <ShieldCheck size={30} />
            <h2>Inquiry tersimpan.</h2>
            <p>WhatsApp sedang dibuka untuk melanjutkan percakapan.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="inquiry-form" data-testid="inquiry-form">
            <label>Nama<input required name="name" onChange={update} data-testid="inquiry-name" placeholder="Nama lengkap" /></label>
            <label>WhatsApp<input required name="whatsapp" onChange={update} data-testid="inquiry-whatsapp" placeholder="08xxxxxxxxxx" /></label>
            <label>Email <span>(optional)</span><input name="email" onChange={update} data-testid="inquiry-email" type="email" placeholder="email@contoh.com" /></label>
            <label>Layanan
              <select name="service" onChange={update} data-testid="inquiry-service" defaultValue="Rental">
                <option>Rental</option><option>Open Trip</option><option>Private Trip</option>
                <option>Corporate Trip</option><option>Vendor Camping</option><option>Wash</option>
                <option>Repair</option><option>Other</option>
              </select>
            </label>
            <label>Cabang (untuk rental)
              <select name="branch" onChange={update} data-testid="inquiry-branch" defaultValue="">
                <option value="">— Pilih bila rental —</option>
                {site.branches.map(b => <option key={b.id} value={b.id}>{b.name.replace("Gembeling Shelter — ", "")}</option>)}
              </select>
            </label>
            <label>Tanggal<input name="date" onChange={update} data-testid="inquiry-date" type="date" /></label>
            <label>Jumlah orang<input name="people" onChange={update} data-testid="inquiry-people" placeholder="Contoh: 4 orang" /></label>
            <label className="full">Catatan<textarea name="notes" onChange={update} data-testid="inquiry-notes" placeholder="Ceritakan kebutuhanmu..." /></label>
            <button className="button primary full" type="submit" data-testid="inquiry-submit">CONTINUE TO WHATSAPP <ArrowUpRight size={17} /></button>
          </form>
        )}
      </section>
    </Shell>
  );
}

function Login() {
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  useSEO("Admin | Gembeling Group", "Admin access.");
  const submit = async e => {
    e.preventDefault();
    const r = await fetch(`${API}/auth/login`, { method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    if (r.ok) nav("/admin"); else setError((await r.json()).detail);
  };
  return (
    <div className="login-page">
      <Link to="/" className="brand" data-testid="login-brand"><Logo branding={{}} /></Link>
      <form onSubmit={submit} className="login-card" data-testid="login-form">
        <p className="eyebrow">ADMIN ACCESS</p>
        <h1>Welcome back.</h1>
        {error && <p className="error" data-testid="login-error">{error}</p>}
        <label>Email<input required type="email" onChange={e => setForm({ ...form, email: e.target.value })} data-testid="login-email" /></label>
        <label>Password<input required type="password" onChange={e => setForm({ ...form, password: e.target.value })} data-testid="login-password" /></label>
        <button className="button primary" type="submit" data-testid="login-submit">LOGIN <ArrowUpRight size={17} /></button>
      </form>
    </div>
  );
}

/* ============ ADMIN DASHBOARD ============ */

const ADMIN_SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "inquiries", label: "Inquiries", icon: Mail },
  { key: "branches", label: "Branches", icon: MapPin },
  { key: "products", label: "Shelter Catalog", icon: Package },
  { key: "trips", label: "Trip Documentation", icon: Compass },
  { key: "services", label: "Service Prices", icon: Wrench },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
  { key: "testimonials", label: "Testimonials", icon: Star },
  { key: "branding", label: "Branding & Content", icon: Palette },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

function Admin() {
  const [auth, setAuth] = useState(null);
  const [section, setSection] = useState("overview");
  const nav = useNavigate();
  useSEO("Admin Dashboard | Gembeling Group", "Control center.");
  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(x => { if (!x) nav("/admin/login"); else setAuth(x); });
  }, [nav]);
  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    nav("/admin/login");
  };
  if (!auth) return <div className="loading" data-testid="admin-loading">Checking access...</div>;
  return (
    <div className="admin-page">
      <aside>
        <Link to="/" className="brand" data-testid="admin-brand"><span className="brand-mark">G</span><span>GEMBELING<br /><b>GROUP</b></span></Link>
        <p className="admin-label">CONTROL CENTER</p>
        <div className="admin-nav">
          {ADMIN_SECTIONS.map(s => (
            <button key={s.key} onClick={() => setSection(s.key)}
              className={section === s.key ? "selected" : ""} data-testid={`admin-nav-${s.key}`}>
              <s.icon size={15} /> {s.label}
            </button>
          ))}
          <Link to="/" data-testid="admin-view-site"><Eye size={15} /> View website</Link>
        </div>
        <button onClick={logout} className="logout" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
      </aside>
      <main className="admin-main">
        <div className="admin-top">
          <div>
            <p className="eyebrow">ADMIN · {section.toUpperCase()}</p>
            <h1>Hello, <em>Gembeling.</em></h1>
          </div>
          <span className="admin-user" data-testid="admin-user">{auth.email}</span>
        </div>
        {section === "overview" && <AdminOverview />}
        {section === "inquiries" && <AdminInquiries />}
        {section === "branches" && <AdminBranches />}
        {section === "products" && <AdminContent collection="products" fields={PRODUCT_FIELDS} title="Shelter Products" />}
        {section === "trips" && <AdminContent collection="trips" fields={TRIP_FIELDS} title="Trip Documentation" />}
        {section === "services" && <AdminContent collection="services" fields={SERVICE_FIELDS} title="Service Prices (Wash · Repair · Vendor Camping)" />}
        {section === "gallery" && <AdminContent collection="gallery" fields={GALLERY_FIELDS} title="Gallery Photos" />}
        {section === "testimonials" && <AdminContent collection="testimonials" fields={TESTIMONIAL_FIELDS} title="Testimonials" />}
        {section === "branding" && <AdminBranding />}
        {section === "settings" && <AdminSettings />}
      </main>
    </div>
  );
}

function useAdminFetch(url) {
  const [data, setData] = useState([]);
  const refresh = useCallback(() => fetch(`${API}${url}`, { credentials: "include" }).then(r => r.ok ? r.json() : []).then(setData), [url]);
  useEffect(() => { refresh(); }, [refresh]);
  return [data, refresh];
}

function AdminOverview() {
  const [inquiries] = useAdminFetch("/admin/inquiries");
  const [branches] = useAdminFetch("/admin/branches");
  const [products] = useAdminFetch("/admin/products");
  const [trips] = useAdminFetch("/admin/trips");
  const cards = [
    { l: "INQUIRIES", v: inquiries.length, s: "all submitted leads" },
    { l: "BRANCHES", v: String(branches.length).padStart(2, "0"), s: "Depok & Cileungsi" },
    { l: "PRODUCTS", v: products.length, s: "equipment items" },
    { l: "TRIPS", v: trips.length, s: "upcoming journeys" },
  ];
  return (
    <>
      <div className="admin-stats">
        {cards.map(c => (
          <div key={c.l}><span>{c.l}</span><strong data-testid={`stat-${c.l.toLowerCase()}`}>{c.v}</strong><small>{c.s}</small></div>
        ))}
      </div>
      <section className="admin-section">
        <div className="section-title">
          <div><p className="eyebrow">LATEST ACTIVITY</p><h2>Recent inquiries</h2></div>
        </div>
        <div className="inquiry-table">
          {inquiries.length === 0 ? (
            <p className="empty-admin" data-testid="inquiries-empty">Belum ada inquiry masuk.</p>
          ) : inquiries.slice(0, 5).map(i => (
            <div className="inquiry-row" key={i.id}>
              <div><b>{i.name}</b><span>{i.service} · {i.whatsapp}</span></div>
              <span>{i.date || "Tanggal fleksibel"}</span>
              <strong className={`pill ${(i.status || "new").toLowerCase()}`}>{i.status}</strong>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function AdminInquiries() {
  const [list, refresh] = useAdminFetch("/admin/inquiries");
  const setStatus = async (id, status) => {
    await fetch(`${API}/admin/inquiries/${id}`, { method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }) });
    refresh();
  };
  const remove = async id => {
    if (!window.confirm("Hapus inquiry ini?")) return;
    await fetch(`${API}/admin/inquiries/${id}`, { method: "DELETE", credentials: "include" });
    refresh();
  };
  return (
    <section className="admin-section no-margin">
      <div className="section-title"><div><p className="eyebrow">INQUIRIES</p><h2>Lead management</h2></div></div>
      <div className="inquiry-table detailed">
        {list.length === 0 ? <p className="empty-admin">Belum ada inquiry.</p> : list.map(i => (
          <div className="inquiry-row full" key={i.id} data-testid={`admin-inquiry-${i.id}`}>
            <div><b>{i.name}</b><span>{i.whatsapp} · {i.email || "no email"}</span></div>
            <div><span>SERVICE</span><b>{i.service}</b></div>
            <div><span>BRANCH / DATE</span><b>{i.branch || "-"} · {i.date || "-"}</b></div>
            <div><span>NOTES</span><p className="row-notes">{i.notes || "-"}</p></div>
            <select value={i.status} onChange={e => setStatus(i.id, e.target.value)} data-testid={`inquiry-status-${i.id}`}>
              {["NEW", "CONTACTED", "CONFIRMED", "COMPLETED", "CANCELLED"].map(s => <option key={s}>{s}</option>)}
            </select>
            <button className="icon-btn danger" onClick={() => remove(i.id)} data-testid={`inquiry-delete-${i.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

const BRANCH_FIELDS = [
  { key: "name", label: "Nama", required: true },
  { key: "description", label: "Deskripsi", type: "textarea" },
  { key: "address", label: "Alamat", required: true, type: "textarea" },
  { key: "maps_url", label: "Google Maps URL" },
  { key: "whatsapp", label: "WhatsApp", required: true },
  { key: "hours", label: "Jam Operasional", required: true },
  { key: "status", label: "Status", type: "select", options: ["ACTIVE", "CLOSED", "MAINTENANCE"] },
];

function AdminBranches() {
  const [list, refresh] = useAdminFetch("/admin/branches");
  const [editing, setEditing] = useState(null);
  const save = async form => {
    await fetch(`${API}/admin/branches/${editing.id}`, { method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setEditing(null); refresh();
  };
  return (
    <section className="admin-section no-margin">
      <div className="section-title"><div><p className="eyebrow">BRANCHES</p><h2>Rental locations</h2></div></div>
      <div className="admin-list">
        {list.map(b => (
          <div key={b.id} className="admin-row" data-testid={`admin-branch-${b.id}`}>
            <div><b>{b.name}</b><span>{b.address}</span><small>{b.whatsapp} · {b.hours}</small></div>
            <button className="button small" onClick={() => setEditing(b)} data-testid={`edit-branch-${b.id}`}>EDIT</button>
          </div>
        ))}
      </div>
      {editing && <Modal title={`Edit ${editing.name}`} onClose={() => setEditing(null)}>
        <RecordForm fields={BRANCH_FIELDS} initial={editing} onSubmit={save} />
      </Modal>}
    </section>
  );
}

const PRODUCT_FIELDS = [
  { key: "name", label: "Nama Produk", required: true },
  { key: "category", label: "Kategori (bisa ketik baru, atau pilih dari daftar)", type: "combo",
    suggestions: ["Tenda", "Sleeping", "Cooking", "Hiking", "Equipment", "Paketan", "Lighting", "Furniture", "Safety"] },
  { key: "branch_id", label: "Cabang (kosongkan untuk tampil di kedua cabang)", type: "branch" },
  { key: "capacity", label: "Kapasitas", placeholder: "2P, 4P, dsb." },
  { key: "price", label: "Harga", placeholder: "Rp 50.000 / malam" },
  { key: "status", label: "Status", type: "select", options: ["AVAILABLE", "BOOKED", "MAINTENANCE"] },
  { key: "image_url", label: "URL Foto" },
  { key: "description", label: "Deskripsi", type: "textarea" },
];
const TRIP_FIELDS = [
  { key: "name", label: "Nama Trip", required: true },
  { key: "destination", label: "Destinasi", placeholder: "Rinjani, Papandayan, dll." },
  { key: "date", label: "Tanggal / Periode", placeholder: "12–14 Mar 2026 · atau 'Maret 2025'" },
  { key: "category", label: "Kategori", type: "select", options: ["OPEN TRIP", "PRIVATE TRIP", "CORPORATE TRIP", "COMMUNITY TRIP", "SCHOOL TRIP"] },
  { key: "image_url", label: "Foto Dokumentasi", required: true },
  { key: "description", label: "Cerita Trip (opsional)", type: "textarea" },
];
const SERVICE_FIELDS = [
  { key: "name", label: "Nama Layanan / Paket", required: true },
  { key: "category", label: "Untuk Section", type: "select", options: ["wash", "repair", "event"], required: true },
  { key: "price", label: "Harga", placeholder: "Rp 50.000 · atau 'Mulai Rp 500rb'", required: true },
  { key: "description", label: "Keterangan (opsional)", type: "textarea" },
];
const GALLERY_FIELDS = [
  { key: "name", label: "Judul", required: true },
  { key: "category", label: "Kategori", type: "select", options: ["Adventure", "Camping", "Hiking", "Event", "Equipment", "Wash", "Repair"] },
  { key: "image_url", label: "URL Foto", required: true },
  { key: "description", label: "Caption", type: "textarea" },
];
const TESTIMONIAL_FIELDS = [
  { key: "name", label: "Nama Pelanggan", required: true },
  { key: "service", label: "Layanan", placeholder: "Open Trip Rinjani / Rental Shelter" },
  { key: "rating", label: "Rating", type: "select", options: ["5", "4", "3", "2", "1"] },
  { key: "description", label: "Testimonial", required: true, type: "textarea" },
];

function AdminContent({ collection, fields, title }) {
  const [list, refresh] = useAdminFetch(`/admin/${collection}`);
  const [editing, setEditing] = useState(null);
  const [branches] = useAdminFetch("/admin/branches");
  const existingValues = useMemo(() => {
    const map = {};
    fields.forEach(f => {
      if (f.type === "combo") {
        map[f.key] = Array.from(new Set([...(f.suggestions || []), ...list.map(x => x[f.key]).filter(Boolean)]));
      }
    });
    return map;
  }, [list, fields]);
  const save = async form => {
    const url = editing.id ? `${API}/admin/${collection}/${editing.id}` : `${API}/admin/${collection}`;
    const method = editing.id ? "PUT" : "POST";
    const payload = { ...form };
    if (payload.rating) payload.rating = parseInt(payload.rating, 10);
    await fetch(url, { method, credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    setEditing(null); refresh();
  };
  const remove = async id => {
    if (!window.confirm("Hapus item ini?")) return;
    await fetch(`${API}/admin/${collection}/${id}`, { method: "DELETE", credentials: "include" });
    refresh();
  };
  return (
    <section className="admin-section no-margin">
      <div className="section-title">
        <div><p className="eyebrow">{collection.toUpperCase()}</p><h2>{title}</h2></div>
        <button className="button primary small" onClick={() => setEditing({})} data-testid={`add-${collection}`}>
          <Plus size={14} /> ADD NEW
        </button>
      </div>
      <div className="admin-list">
        {list.length === 0 ? (
          <p className="empty-admin" data-testid={`${collection}-empty`}>Belum ada data. Klik ADD NEW untuk menambahkan.</p>
        ) : list.map(item => (
          <div key={item.id} className="admin-row" data-testid={`admin-${collection}-${item.id}`}>
            {item.image_url && <img src={resolveImage(item.image_url, "")} alt={item.name} className="row-thumb" />}
            <div>
              <b>{item.name}</b>
              <span>{item.category}{item.destination ? ` · ${item.destination}` : ""}{item.branch_id ? ` · ${item.branch_id}` : (collection === "products" ? " · semua cabang" : "")}</span>
              <small>{item.price || item.date || item.service || ""} {item.status && `· ${item.status}`}</small>
            </div>
            <div className="row-actions">
              <button className="button small" onClick={() => setEditing(item)} data-testid={`edit-${collection}-${item.id}`}>EDIT</button>
              <button className="icon-btn danger" onClick={() => remove(item.id)} data-testid={`delete-${collection}-${item.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <Modal title={editing.id ? `Edit ${editing.name || title}` : `Add new ${title}`} onClose={() => setEditing(null)}>
        <RecordForm fields={fields} initial={editing} branches={branches} existingValues={existingValues} onSubmit={save} />
      </Modal>}
    </section>
  );
}

function AdminSettings() {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    fetch(`${API}/admin/settings`, { credentials: "include" }).then(r => r.json()).then(d => { delete d.key; setForm(d); });
  }, []);
  if (!form) return <div className="empty-admin">Loading...</div>;
  const update = e => setForm({ ...form, [e.target.name]: e.target.value });
  const save = async e => {
    e.preventDefault();
    await fetch(`${API}/admin/settings`, { method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  const fields = [
    { key: "whatsapp", label: "WhatsApp Utama" },
    { key: "instagram", label: "Instagram Handle" },
    { key: "email", label: "Email" },
    { key: "hours", label: "Jam Operasional" },
    { key: "address", label: "Alamat Utama", type: "textarea" },
    { key: "maps_url", label: "Google Maps URL" },
    { key: "vision", label: "Visi", type: "textarea" },
    { key: "mission", label: "Misi", type: "textarea" },
  ];
  return (
    <section className="admin-section no-margin">
      <div className="section-title"><div><p className="eyebrow">SETTINGS</p><h2>Group settings</h2></div></div>
      <form onSubmit={save} className="settings-form" data-testid="settings-form">
        {fields.map(f => (
          <label key={f.key} className={f.type === "textarea" ? "full" : ""}>
            {f.label}
            {f.type === "textarea" ?
              <textarea name={f.key} value={form[f.key] || ""} onChange={update} data-testid={`settings-${f.key}`} /> :
              <input name={f.key} value={form[f.key] || ""} onChange={update} data-testid={`settings-${f.key}`} />}
          </label>
        ))}
        <button className="button primary full" type="submit" data-testid="settings-save">
          <Save size={14} /> {saved ? "SAVED!" : "SAVE SETTINGS"}
        </button>
      </form>
    </section>
  );
}

function AdminBranding() {
  const [state, setState] = useState(null);
  const [status, setStatus] = useState("");
  const refresh = useCallback(() => {
    fetch(`${API}/admin/branding`, { credentials: "include" }).then(r => r.json()).then(setState);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  if (!state) return <div className="empty-admin">Loading...</div>;
  const draft = state.draft;
  const live = state.live;
  const update = (key, val) => setState({ ...state, draft: { ...draft, [key]: val } });
  const saveDraft = async () => {
    setStatus("saving");
    const r = await fetch(`${API}/admin/branding/draft`, { method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
    const ok = r.ok;
    if (ok) { setStatus("saved"); setTimeout(() => setStatus(""), 1800); refresh(); }
    else { setStatus(""); window.alert("Gagal menyimpan draft."); }
    return ok;
  };
  const publish = async () => {
    if (!window.confirm("Publish perubahan ke website publik?")) return;
    const saved = await saveDraft();
    if (!saved) return;
    const r = await fetch(`${API}/admin/branding/publish`, { method: "POST", credentials: "include" });
    if (!r.ok) { window.alert("Gagal publish."); return; }
    setStatus("published"); setTimeout(() => setStatus(""), 2500);
    refresh();
  };
  const revert = async () => {
    if (!window.confirm("Buang perubahan draft dan kembalikan ke versi live?")) return;
    await fetch(`${API}/admin/branding/revert`, { method: "POST", credentials: "include" });
    refresh();
  };
  const hasChanges = JSON.stringify(draft) !== JSON.stringify(live);

  const FONTS = [
    { key: "classic", label: "Classic", preview: "Georgia + Trebuchet" },
    { key: "editorial", label: "Editorial", preview: "Playfair Display + Inter" },
    { key: "rugged", label: "Rugged", preview: "Bitter + Barlow" },
    { key: "modern", label: "Modern", preview: "Fraunces + Space Grotesk" },
  ];
  const IMG_FIELDS = [
    { key: "hero_image_url", label: "Hero Homepage" },
    { key: "about_image_url", label: "About / Image Band" },
    { key: "shelter_image_url", label: "Shelter Hero" },
    { key: "adventure_image_url", label: "Adventure Hero" },
    { key: "wash_image_url", label: "Wash Hero" },
    { key: "repair_image_url", label: "Repair Hero" },
    { key: "event_image_url", label: "Vendor Camping Hero" },
    { key: "gallery_image_url", label: "Gallery Hero" },
  ];

  return (
    <section className="admin-section no-margin">
      <div className="section-title">
        <div><p className="eyebrow">BRANDING & CONTENT</p><h2>Logo · Hero · Photos · Font</h2></div>
        <div className="branding-actions">
          {hasChanges && <span className="chip pending" data-testid="draft-status">● DRAFT belum di-publish</span>}
          {!hasChanges && <span className="chip live">● IN SYNC WITH LIVE</span>}
          <button className="button small" onClick={revert} data-testid="branding-revert"><RotateCcw size={13} /> REVERT</button>
          <button className="button small" onClick={saveDraft} data-testid="branding-save"><Save size={13} /> SAVE DRAFT</button>
          <button className="button primary small" onClick={publish} data-testid="branding-publish"><Globe size={13} /> PUBLISH</button>
        </div>
      </div>
      {status && <div className={`toast ${status}`} data-testid="branding-toast">{status === "saved" ? "Draft tersimpan." : status === "published" ? "Berhasil publish ke website publik." : "Menyimpan..."}</div>}

      <div className="branding-grid">
        <div className="branding-block">
          <h3>Logo</h3>
          <label>Logo text — bagian depan
            <input value={draft.logo_text_primary || ""} onChange={e => update("logo_text_primary", e.target.value)} data-testid="brand-logo-primary" placeholder="GEMBELING" />
          </label>
          <label>Logo text — bagian belakang
            <input value={draft.logo_text_secondary || ""} onChange={e => update("logo_text_secondary", e.target.value)} data-testid="brand-logo-secondary" placeholder="GROUP" />
          </label>
          <ImageField label="Logo image (opsional, menggantikan teks)" value={draft.logo_image_url}
            onChange={v => update("logo_image_url", v)} testid="logo-image" />
        </div>

        <div className="branding-block">
          <h3>Hero Homepage</h3>
          <label>Hero eyebrow <span className="hint">(tagline kecil di atas judul; koma otomatis jadi titik tengah `·`)</span>
            <input value={draft.hero_eyebrow || ""} onChange={e => update("hero_eyebrow", e.target.value)}
              data-testid="brand-hero-eyebrow" placeholder="Rent Equipment, Trip Organizer, Wash, Repair, Vendor Camping" />
          </label>
          <label>Headline <span className="hint">(gunakan *italic* untuk kata miring warna lime, enter untuk baris baru)</span>
            <textarea value={draft.hero_headline || ""} onChange={e => update("hero_headline", e.target.value)}
              rows={3} data-testid="brand-hero-headline" placeholder="Built for *adventure.* Ready for the journey." />
          </label>
          <label>Subheadline (deskripsi di bawah headline)
            <textarea value={draft.hero_subheadline || ""} onChange={e => update("hero_subheadline", e.target.value)}
              rows={3} data-testid="brand-hero-subheadline" />
          </label>
        </div>

        <div className="branding-block">
          <h3>Font Preset</h3>
          <div className="font-grid">
            {FONTS.map(f => (
              <button key={f.key} type="button" onClick={() => update("font_preset", f.key)}
                className={`font-card ${draft.font_preset === f.key ? "selected" : ""}`}
                data-testid={`font-${f.key}`} data-font={f.key}>
                <b className="font-serif">{f.label}</b>
                <span>{f.preview}</span>
                <small>The quick brown fox</small>
              </button>
            ))}
          </div>
        </div>

        <div className="branding-block full">
          <h3>Hero & Section Photos</h3>
          <div className="img-grid">
            {IMG_FIELDS.map(f => (
              <ImageField key={f.key} label={f.label} value={draft[f.key]}
                onChange={v => update(f.key, v)} testid={f.key} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const upload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/admin/upload`, { method: "POST", credentials: "include", body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || "upload gagal");
      const data = await r.json();
      onChange(data.url);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const preview = value ? (value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value) : null;
  return (
    <label className="image-field">
      <span className="field-label">{label}</span>
      <div className="image-field-row">
        <div className="preview-box">
          {preview ? <img src={preview} alt={label} data-testid={`preview-${testid}`} /> : <ImageIcon size={22} />}
        </div>
        <div className="image-field-controls">
          <input type="text" value={value || ""} onChange={e => onChange(e.target.value)}
            placeholder="URL foto atau upload file" data-testid={`url-${testid}`} />
          <div className="image-field-buttons">
            <label className="button small upload-btn" data-testid={`upload-${testid}`}>
              <Upload size={13} /> {uploading ? "Uploading…" : "UPLOAD"}
              <input type="file" accept="image/*" onChange={upload} hidden />
            </label>
            {value && <button type="button" className="icon-btn" onClick={() => onChange("")}><X size={14} /></button>}
          </div>
          {err && <small className="err-text">{err}</small>}
        </div>
      </div>
    </label>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button onClick={onClose} className="icon-btn" data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function RecordForm({ fields, initial, branches = [], existingValues = {}, onSubmit }) {
  const [form, setForm] = useState(initial || {});
  const update = e => setForm({ ...form, [e.target.name]: e.target.value });
  const setField = (key, val) => setForm({ ...form, [key]: val });
  const submit = e => { e.preventDefault(); onSubmit(form); };
  return (
    <form onSubmit={submit} className="record-form" data-testid="record-form">
      {fields.map(f => {
        if (f.key === "image_url") {
          return (
            <div key={f.key} className="full">
              <ImageField label={f.label || "Foto"} value={form[f.key]}
                onChange={v => setField(f.key, v)} testid={`record-${f.key}`} />
            </div>
          );
        }
        return (
          <label key={f.key} className={f.type === "textarea" || f.type === "combo" ? "full" : ""}>
            {f.label}{f.required && <span className="req"> *</span>}
            {f.type === "textarea" ? (
              <textarea name={f.key} value={form[f.key] || ""} onChange={update} required={f.required} data-testid={`field-${f.key}`} />
            ) : f.type === "select" ? (
              <select name={f.key} value={form[f.key] || ""} onChange={update} data-testid={`field-${f.key}`}>
                <option value="">—</option>
                {f.options.map(o => <option key={o}>{o}</option>)}
              </select>
            ) : f.type === "combo" ? (
              <>
                <input list={`dl-${f.key}`} name={f.key} value={form[f.key] || ""} onChange={update}
                  placeholder="Ketik baru atau pilih dari daftar" required={f.required}
                  data-testid={`field-${f.key}`} />
                <datalist id={`dl-${f.key}`}>
                  {(existingValues[f.key] || f.suggestions || []).map(o => <option key={o} value={o} />)}
                </datalist>
              </>
            ) : f.type === "branch" ? (
              <select name={f.key} value={form[f.key] || ""} onChange={update} data-testid={`field-${f.key}`}>
                <option value="">Semua cabang (otomatis Depok + Cileungsi)</option>
                {branches.map(b => <option key={b.id} value={b.id}>Khusus — {b.name}</option>)}
              </select>
            ) : (
              <input name={f.key} value={form[f.key] || ""} onChange={update} placeholder={f.placeholder || ""} required={f.required} data-testid={`field-${f.key}`} />
            )}
          </label>
        );
      })}
      <button className="button primary full" type="submit" data-testid="record-save"><Save size={14} /> SAVE</button>
    </form>
  );
}

function UnitRouter() {
  const { slug } = useParams();
  if (slug === "shelter") return <ShelterPage />;
  if (slug === "adventure") return <AdventurePage />;
  if (["wash", "repair", "event"].includes(slug)) return <ServicePage type={slug} />;
  return <Home />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/inquiry" element={<Inquiry />} />
        <Route path="/admin/login" element={<Login />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/:slug" element={<UnitRouter />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
